import { memo } from 'react';
import OverflowChips from '../../components/OverflowChips';
import FindingRefChip from './FindingRefChip';
import { type FindingRefInfo } from './findingRefLabels';

interface FindingRefsCellProps {
    ids: string[];
    resolver: Map<string, FindingRefInfo>;
}

/** Same references, in the same order — the grid rebuilds the array, not the set. */
function sameRefs(before: FindingRefsCellProps, after: FindingRefsCellProps) {
    if (before.resolver !== after.resolver) {
        return false;
    }
    if (before.ids.length !== after.ids.length) {
        return false;
    }
    return before.ids.every((id, index) => id === after.ids[index]);
}

/**
 * The grid cell for a record's Befund references: one linking chip per ref,
 * only as many as fit, then a "+N" chip that opens a popover with all of them
 * (see OverflowChips). Direct navigation — a grid is not an editor.
 *
 * Compared by VALUE rather than by identity: the column's `valueGetter` merges
 * three id arrays into a fresh one on every call, so a plain `memo` would never
 * hold.
 */
const FindingRefsCell = memo(function FindingRefsCell({
    ids,
    resolver
}: FindingRefsCellProps) {
    const items = ids.map((id) => {
        const info = resolver.get(id);
        return { id, label: info?.label ?? id, to: info?.to };
    });
    return (
        <OverflowChips
            items={items}
            getKey={(item) => item.id}
            renderChip={(item) => (
                <FindingRefChip label={item.label} to={item.to} />
            )}
        />
    );
}, sameRefs);

export default FindingRefsCell;
